import type { SettingsBackend } from '@slideforge/shared/store/types.js';
import { REQUIREMENTS, isOfficeAvailable, isSupported } from './capabilities.js';

/**
 * The `SettingsBackend` the shared `CourseStore` writes through. This is the
 * only place in the repo that touches `Office.context.document.settings`.
 *
 * Requirement: `Settings 1.1`. Degradation: `createSettingsBackend` returns an
 * in-memory backend so the pane still opens and the author can read an existing
 * course, with the caller showing "changes cannot be saved in this version".
 */

/**
 * SPIKE 2 finding: no size limit is documented anywhere, and the practical
 * ceiling reported in the wild is around 1 MB for the whole settings bag. The
 * number is a measured estimate, not a specification, so the store treats
 * approaching it as a first-class path rather than trusting it exactly.
 */
export const MEASURED_SETTINGS_CAPACITY_BYTES = 1_000_000;

export function createSettingsBackend(
  capacityBytes = MEASURED_SETTINGS_CAPACITY_BYTES,
): SettingsBackend {
  if (!isOfficeAvailable() || !isSupported(REQUIREMENTS.settings)) {
    return createMemoryBackend(capacityBytes);
  }

  return {
    capacityBytes,

    read(key: string): unknown {
      try {
        return Office.context.document.settings.get(key);
      } catch {
        return undefined;
      }
    },

    write(key: string, value: unknown): void {
      Office.context.document.settings.set(key, value);
    },

    remove(key: string): void {
      Office.context.document.settings.remove(key);
    },

    save(): Promise<void> {
      return new Promise((resolve, reject) => {
        try {
          Office.context.document.settings.saveAsync((result) => {
            if (result.status === Office.AsyncResultStatus.Failed) {
              reject(
                new Error(
                  result.error?.message ??
                    'PowerPoint refused to save the SlideForge data in this presentation.',
                ),
              );
              return;
            }
            resolve();
          });
        } catch (error) {
          reject(error instanceof Error ? error : new Error(String(error)));
        }
      });
    },
  };
}

/** Used outside Office and in tests. Reads work; writes are lost on close. */
export function createMemoryBackend(capacityBytes = MEASURED_SETTINGS_CAPACITY_BYTES): SettingsBackend {
  const bag = new Map<string, unknown>();
  return {
    capacityBytes,
    read: (key) => bag.get(key),
    write: (key, value) => {
      bag.set(key, value);
    },
    remove: (key) => {
      bag.delete(key);
    },
    save: async () => {
      // Nothing to persist; the caller has already been told this is in-memory.
    },
  };
}

export function settingsAreDurable(): boolean {
  return isOfficeAvailable() && isSupported(REQUIREMENTS.settings);
}
